import { useEffect, useRef, useState } from 'react';
import { confirmUserBeforeTrigger } from '@/utils/confirmUserBeforeTrigger';

export default function useConfirmedUserTrigger({
  enabled = true,
  isInitialized = true,
  user,
  refreshUser,
  shouldTrigger,
  triggerKey = 'default',
}) {
  const [shouldRunTrigger, setShouldRunTrigger] = useState(false);
  const [isConfirmingTrigger, setIsConfirmingTrigger] = useState(false);
  const confirmedTriggerKeyRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const syncTrigger = async () => {
      if (!enabled || !isInitialized || !user) {
        confirmedTriggerKeyRef.current = null;
        setShouldRunTrigger(false);
        setIsConfirmingTrigger(false);
        return;
      }

      if (!shouldTrigger(user)) {
        confirmedTriggerKeyRef.current = null;
        setShouldRunTrigger(false);
        setIsConfirmingTrigger(false);
        return;
      }

      if (!refreshUser || confirmedTriggerKeyRef.current === triggerKey) {
        setShouldRunTrigger(true);
        setIsConfirmingTrigger(false);
        return;
      }

      confirmedTriggerKeyRef.current = triggerKey;
      setShouldRunTrigger(false);
      setIsConfirmingTrigger(true);

      try {
        const result = await confirmUserBeforeTrigger({
          currentValue: user,
          shouldTrigger,
          refresh: () => refreshUser({ forceUpdate: true }),
        });

        if (isMounted) setShouldRunTrigger(result.shouldTrigger);
      } catch (error) {
        if (isMounted) setShouldRunTrigger(true);
      } finally {
        if (isMounted) setIsConfirmingTrigger(false);
      }
    };

    syncTrigger();

    return () => {
      isMounted = false;
    };
  }, [enabled, isInitialized, refreshUser, shouldTrigger, triggerKey, user]);

  return {
    shouldRunTrigger,
    isConfirmingTrigger,
  };
}
